import React, { useEffect, useState, useMemo } from 'react';

import FormSearchHotel from '@shared/pages/Hotels/FormSearchHotel';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import TabsHeader from '@shared/components/TabsHeader';

import HotelSearchResults from './HotelSearchResults';
import NoResultsHotelSearch from './components/NoResultsHotelSearch';

import { HOTEL_DETAIL } from '@shared/helpers/routes';

import sortFavoritesClient from '@shared/helpers/hotels/sortFavoritesClient';
import sortHotelsClient from '@shared/helpers/hotels/sortHotelsClient';

import { useProviderPriorities } from '@HotelSearchHooks/useProviderPriorities';

const HotelSearch = () => {
  const history = useHistory();
  const searchInfo = useSelector(store => store.search);

  const { priorities } = useProviderPriorities();

  const [isLoadingResults, setIsLoadingResults] = useState(true);
  const [isLoadingFastResults, setIsLoadingFastResults] = useState(true);

  const [hotReferenceCities, setHotReferenceCities] = useState(null);

  const [hotelSearchResults, setHotelSearchResults] = useState({
    hotels: [],
    tokens: [],
    fav_messages: [],
    has_favorites: false,
  });

  const [hotelFastSearchResults, setHotelFastSearchResults] = useState({
    hotels: [],
    tokens: [],
  });

  useEffect(() => {
    if (!searchInfo.checkIn && !searchInfo.checkOut) {
      history.push('/hotels');
    }
  }, []);

  useEffect(() => {
    if (
      searchInfo?.search_option?.type === 'hotel' &&
      hotelSearchResults?.hotels.length === 1
    ) {
      const urlIntegrationId = hotelSearchResults.hotels[0].id_integration;
      const urlToken = hotelSearchResults?.tokens?.[0]?.split(
        `${urlIntegrationId}-`
      )[1];
      const urlHotelId = hotelSearchResults.hotels[0].id_hotel;
      const urlHotelCode = hotelSearchResults.hotels[0].code;
      const urlIdCity = hotelSearchResults.hotels[0].id_city;
      const urlIdProvider = hotelSearchResults.hotels[0].id_provider ?? null;

      const path =
        HOTEL_DETAIL +
        `?token=${urlToken}&integration=${urlIntegrationId}&hotel=${urlHotelId}&hotel_code=${urlHotelCode}&city=${urlIdCity}&id_provider=${urlIdProvider}`;

      history.push(path);
    }
  }, [
    searchInfo?.search_option?.type,
    hotelSearchResults?.hotels,
    hotelSearchResults?.tokens,
  ]);

  const favConfigMap = useMemo(
    () =>
      (hotelSearchResults?.fav_messages || []).reduce((acc, el) => {
        acc[el.id] = el;
        return acc;
      }, {}),
    [hotelSearchResults?.fav_messages]
  );

  const getFavConfig = id => favConfigMap[id];

  const hasFinalResults =
    !isLoadingResults && hotelSearchResults.hotels.length > 0;

  const hasFastResults =
    !isLoadingFastResults && hotelFastSearchResults.hotels.length > 0;

  const hotelsToShow = useMemo(() => {
    const mainHotels = hotelSearchResults.hotels || [];
    const fastHotels = hotelFastSearchResults.hotels || [];

    const allHotels = [
      ...mainHotels.map(h => ({ ...h, __isFast: false })),
      ...fastHotels
        .filter(h => !mainHotels.some(m => m.id_hotel === h.id_hotel))
        .map(h => ({ ...h, __isFast: true })),
    ];

    const favorites = allHotels.filter(h => h.id_favorite != null);
    const nonFavorites = allHotels.filter(h => h.id_favorite == null);

    // fallback seguro: si no hay priorities, pasar objeto vacío
    const safePriorities =
      priorities && Object.keys(priorities).length > 0 ? priorities : {};

    const sortedFavorites = sortFavoritesClient(favorites, safePriorities);
    const sortedNonFavorites = sortHotelsClient(nonFavorites, safePriorities);

    return [...sortedFavorites, ...sortedNonFavorites];
  }, [hotelSearchResults.hotels, hotelFastSearchResults.hotels, priorities]);

  const tokenMap = useMemo(() => {
    const map = new Map();

    const mainTokens = hotelSearchResults.tokens || [];
    mainTokens.forEach(fullToken => {
      const separatorIndex = fullToken.indexOf('-');
      if (separatorIndex > -1) {
        const idIntegration = fullToken.slice(0, separatorIndex);
        const token = fullToken.slice(separatorIndex + 1);
        map.set(`normal-${idIntegration}`, token);
      }
    });

    const fastTokens = hotelFastSearchResults.tokens || [];
    fastTokens.forEach(fullToken => {
      const separatorIndex = fullToken.indexOf('-');
      if (separatorIndex > -1) {
        const idIntegration = fullToken.slice(0, separatorIndex);
        const token = fullToken.slice(separatorIndex + 1);
        map.set(`fast-${idIntegration}`, token);
      }
    });

    return map;
  }, [hotelSearchResults.tokens, hotelFastSearchResults.tokens]);

  return (
    <>
      <TabsHeader site={1} />
      <FormSearchHotel
        navigatesToSearch={false}
        setIsLoadingResults={setIsLoadingResults}
        setIsLoadingFastResults={setIsLoadingFastResults}
        setHotelSearchResults={setHotelSearchResults}
        setHotelFastSearchResults={setHotelFastSearchResults}
        setHotReferenceCities={setHotReferenceCities}
      />
      {hotelsToShow.length > 0 || isLoadingFastResults || isLoadingResults ? (
        <HotelSearchResults
          hotelSearchResults={{
            hotels: hotelsToShow,
            tokenMap,
            fav_messages: hotelSearchResults.fav_messages ?? [],
            has_favorites: hotelSearchResults.has_favorites ?? false,
          }}
          total_passengers={searchInfo?.totalPassengers}
          totalNights={searchInfo?.totalNights}
          citySelected={searchInfo?.city}
          hasFinalResults={hasFinalResults}
          hasFastResults={hasFastResults}
          getFavConfig={getFavConfig}
          isLoadingResults={isLoadingResults}
          hotReferenceCities={hotReferenceCities}
          searchInfo={searchInfo}
        />
      ) : (
        <NoResultsHotelSearch city={searchInfo?.city.label} />
      )}
    </>
  );
};

export default HotelSearch;
